'use client';

import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type ElementPosition = {
  isAboveViewport: boolean;
  isBelowViewport: boolean;
  isInViewport: boolean;
};

type UseElementPositionOptions = {
  initiallyVisible?: boolean;
  enabled?: boolean;
};

export function useElementPosition(
  ref: RefObject<HTMLElement | null>,
  options: UseElementPositionOptions = {},
): ElementPosition {
  const { initiallyVisible = false, enabled = true } = options;

  const [position, setPosition] = useState<ElementPosition>({
    isAboveViewport: false,
    isBelowViewport: !initiallyVisible,
    isInViewport: initiallyVisible,
  });

  const rafId = useRef<number | null>(null);
  const ticking = useRef(false);

  const checkPosition = useCallback(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    setPosition({
      isAboveViewport: rect.bottom < 0,
      isBelowViewport: rect.top > viewportHeight,
      isInViewport: rect.top < viewportHeight && rect.bottom > 0,
    });
  }, [ref]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    checkPosition();

    const handleScroll = () => {
      if (!ticking.current) {
        rafId.current = requestAnimationFrame(() => {
          checkPosition();
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [checkPosition, enabled]);

  return position;
}
